import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { HeadingOutlineItem } from './editor/headingOutline';

/** Observa os headings do artigo (por id) e devolve o id da seção visível
 *  mais próxima do topo — recalcula quando a lista de ids muda (post
 *  carregado, ou conteúdo do editor mudando na prévia). */
function useActiveHeadingId(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    if (!key) {
      setActiveId(null);
      return;
    }
    const elements = key
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [key]);

  return activeId;
}

/** Sumário lateral (títulos/subtítulos do corpo do artigo) com destaque da
 *  seção visível — mesmo componente na página pública e na prévia do editor.
 *  Não renderiza nada se o artigo não tiver nenhum heading. */
export function ArticleOutline({ items, className }: { items: HeadingOutlineItem[]; className?: string }) {
  const activeId = useActiveHeadingId(items.map((item) => item.id));

  if (items.length === 0) return null;

  return (
    <nav className={cn('sticky top-24 text-sm', className)} aria-label="Sumário do artigo">
      <p className="text-xs font-semibold uppercase tracking-widest text-reading/45 mb-3">Neste artigo</p>
      <ul className="space-y-2.5 border-l border-reading/15">
        {items.map((item) => (
          <li key={item.id} style={{ paddingLeft: item.level === 2 ? '1.5rem' : '0.9rem' }}>
            <a
              href={`#${item.id}`}
              className={cn(
                'block leading-snug transition-colors hover:text-reading',
                activeId === item.id ? 'text-reading font-medium' : 'text-reading/50'
              )}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
